import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useFormWithValidation } from '../../../../../app/infrastructure/form';
import { setMessage } from '../../../../../app/reducers/message-slice';
import { usePublicConfiguration } from '../../../../configuration';
import { readApiError } from '../../../../auth/application/readApiError';
import { ContactFormMapper } from '../../../application/contactForm.mapper';
import type { ContactFormValues } from '../../../application/types';
import { resolveContactRowsFromConfig } from '../../../application/contactPolicy';
import { sendFeedback } from '../../../infrastructure/api/feedback.api';
import { contactFormSchema } from './schema/contact-form.schema';

export function useContactPanel() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { configuration } = usePublicConfiguration();
  const contactRows = resolveContactRowsFromConfig(configuration.publicContacts);

  const { form, isRequired, isSubmitted, confirmHandler } =
    useFormWithValidation<ContactFormValues>({
      schema: contactFormSchema,
      onSubmit: async (values, { resetForm, resetSubmitted }) => {
        await sendFeedback(ContactFormMapper.toFeedbackPayload(values))
          .then(() => {
            resetForm();
            resetSubmitted();
            dispatch(
              setMessage({
                value: t('contact-panel.toast.success'),
                type: 'success',
                timeout: 5000,
              }),
            );
          })
          .catch((err: unknown) => {
            const { message } = readApiError(err);
            dispatch(
              setMessage({
                value: message ?? t('contact-panel.toast.error'),
                type: 'error',
                timeout: 7000,
              }),
            );
          });
      },
    });

  return {
    t,
    contactRows,
    form,
    isRequired,
    isSubmitted,
    confirmHandler,
    isSubmitting: form.isSubmitting,
    schema: contactFormSchema,
  };
}
